import {
  COUNTRIES,
  ECountry,
  EProvinceExtension,
  EStreetType,
  PROVINCE_MAP,
  STREET_TYPE_KEYWORDS
} from '@constants';
import { useCallback, useEffect, useRef, useState } from 'react';

import { GoogleAutocompleteRequest, GooglePlace } from '../../../types/custom';

import { INormalizedAddress } from '@/types';

export interface AddressSuggestion {
  id: string;
  displayName: string;
  normalizedAddress: INormalizedAddress;
}

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;
const GOOGLE_PLACES_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

let googleMapsLoadPromise: Promise<void> | null = null;

const loadGoogleMapsScript = (): Promise<void> => {
  if (window.google?.maps?.places?.AutocompleteSuggestion) {
    return Promise.resolve();
  }

  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
  if (existingScript) {
    googleMapsLoadPromise = new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (window.google?.maps?.places?.AutocompleteSuggestion) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);

      setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error('Timeout esperando carga de Google Maps'));
      }, 10000);
    });
    return googleMapsLoadPromise;
  }

  googleMapsLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places&language=es&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      googleMapsLoadPromise = null;
      reject(new Error('Error al cargar Google Maps'));
    };
    document.head.appendChild(script);
  });

  return googleMapsLoadPromise;
};

const getCountryFromCode = (code: string): ECountry | undefined => {
  const upperCode = code.toUpperCase() as ECountry;
  return COUNTRIES.includes(upperCode) ? upperCode : undefined;
};

const detectStreetType = (streetName: string): EStreetType | undefined => {
  const lowerName = streetName.toLowerCase();

  for (const [keyword, streetType] of Object.entries(STREET_TYPE_KEYWORDS)) {
    if (lowerName.startsWith(`${keyword} `) || lowerName.startsWith(`${keyword}.`)) {
      return streetType;
    }
  }

  return undefined;
};

const extractStreetName = (fullStreetName: string): string => {
  const lowerName = fullStreetName.toLowerCase();

  for (const keyword of Object.keys(STREET_TYPE_KEYWORDS)) {
    if (lowerName.startsWith(`${keyword} `)) {
      return fullStreetName.substring(keyword.length + 1).trim();
    }
    if (lowerName.startsWith(`${keyword}.`)) {
      return fullStreetName.substring(keyword.length + 1).trim();
    }
  }

  return fullStreetName;
};

const parseGooglePlace = (place: GooglePlace): INormalizedAddress => {
  const normalized: INormalizedAddress = {};
  const addressComponents = place.addressComponents;

  if (!addressComponents || addressComponents.length === 0) {
    return normalized;
  }

  const getComponent = (types: string[]) => {
    return addressComponents.find(component => types.some(type => component.types.includes(type)));
  };

  const countryComponent = getComponent(['country']);
  if (countryComponent?.shortText) {
    const countryCode = countryComponent.shortText.toUpperCase();
    normalized.countryResidence = getCountryFromCode(countryCode);
    const isSpain = countryCode === ECountry.ES;

    if (isSpain) {
      const level2Name = getComponent(['administrative_area_level_2'])?.longText;
      const level1Name = getComponent(['administrative_area_level_1'])?.longText;
      const mappedProvince =
        (level2Name && PROVINCE_MAP[level2Name]) || (level1Name && PROVINCE_MAP[level1Name]);
      if (mappedProvince) {
        normalized.province = mappedProvince;
      }
    } else {
      normalized.province = EProvinceExtension.OTHER_COUNTRIES;
    }

    const cityComponent =
      getComponent(['locality']) ||
      getComponent(['administrative_area_level_2']) ||
      getComponent(['administrative_area_level_3']);
    if (cityComponent?.longText) {
      normalized.city = cityComponent.longText;
      normalized.residenceLocality = cityComponent.longText;
    }

    const postalCodeComponent = getComponent(['postal_code']);
    if (postalCodeComponent?.longText) {
      normalized.postalCode = postalCodeComponent.longText;
    }

    const routeComponent = getComponent(['route']);
    if (routeComponent?.longText) {
      const routeName = routeComponent.longText;
      normalized.address = extractStreetName(routeName);
      normalized.streetType = detectStreetType(routeName);

      if (!normalized.streetType && !isSpain) {
        normalized.streetType = EStreetType.THOROUGHFARE;
      }
    }

    const streetNumberComponent = getComponent(['street_number']);
    if (streetNumberComponent?.longText) {
      normalized.streetNumber = streetNumberComponent.longText;
    }
  }

  return normalized;
};

export const useAddressAutocomplete = () => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const sessionTokenRef = useRef<unknown | null>(null);

  useEffect(() => {
    let isMounted = true;

    loadGoogleMapsScript().then(() => {
      if (!isMounted) return;

      let attempts = 0;
      const maxAttempts = 10;
      const checkInterval = setInterval(() => {
        if (!isMounted) {
          clearInterval(checkInterval);
          return;
        }

        attempts++;
        if (window.google?.maps?.places?.AutocompleteSuggestion) {
          clearInterval(checkInterval);
          if (window.google.maps.places.AutocompleteSessionToken) {
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
          }
          setIsGoogleMapsLoaded(true);
          setError(null);
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
        }
      }, 200);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const searchAddresses = useCallback(
    async (query: string, countryCode?: string) => {
      if (query.length < MIN_QUERY_LENGTH) {
        setSuggestions([]);
        return;
      }
      if (!isGoogleMapsLoaded) {
        await new Promise(resolve => setTimeout(resolve, 1000));

        if (!window.google?.maps?.places?.AutocompleteSuggestion) {
          setError('GOOGLE_MAPS_NOT_READY');
          setIsLoading(false);
          return;
        } else {
          setIsGoogleMapsLoaded(true);
          if (window.google.maps.places.AutocompleteSessionToken) {
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
          }
        }
      }

      if (!window.google?.maps?.places?.AutocompleteSuggestion) {
        setError('GOOGLE_MAPS_NOT_READY');
        setIsLoading(false);
        return;
      }

      if (error) setError(null);

      setIsLoading(true);

      try {
        if (!sessionTokenRef.current && window.google.maps.places.AutocompleteSessionToken) {
          sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }

        const request: GoogleAutocompleteRequest = {
          input: query,
          language: 'es',
          sessionToken: sessionTokenRef.current
        };

        if (countryCode) {
          request.includedRegionCodes = [countryCode.toLowerCase()];
        }

        const { suggestions: autocompleteSuggestions } =
          await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
            request
          );

        const detailsPromises = autocompleteSuggestions.slice(0, 5).map(async suggestion => {
          try {
            const placeId = suggestion.placePrediction.placeId;
            const place = suggestion.placePrediction.toPlace();
            await place.fetchFields({
              fields: ['formattedAddress', 'addressComponents', 'location']
            });
            return { place, placeId };
          } catch (fetchError) {
            console.error('Error al obtener detalles del lugar:', fetchError);
            return null;
          }
        });

        const detailsResults = await Promise.all(detailsPromises);
        const validResults = detailsResults.filter(
          (result): result is { place: GooglePlace; placeId: string } => result !== null
        );

        const mappedSuggestions: AddressSuggestion[] = validResults.map(({ place, placeId }) => ({
          id: placeId,
          displayName: place.formattedAddress || '',
          normalizedAddress: parseGooglePlace(place)
        }));

        setSuggestions(mappedSuggestions);
        if (mappedSuggestions.length === 0) {
          setError('NO_SUGGESTIONS');
        }
      } catch (autocompleteError) {
        console.error('Error en la búsqueda de direcciones:', autocompleteError);
        setError('SEARCH_ERROR');
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [isGoogleMapsLoaded]
  );

  const debouncedSearch = useCallback(
    (query: string, countryCode?: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        searchAddresses(query, countryCode);
      }, DEBOUNCE_MS);
    },
    [searchAddresses]
  );

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (window.google?.maps?.places?.AutocompleteSessionToken) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
  }, []);

  return {
    suggestions,
    isLoading,
    searchAddresses: debouncedSearch,
    clearSuggestions,
    error,
    isGoogleMapsAvailable: isGoogleMapsLoaded && !error
  };
};
